import type { TimelineItem } from '@/types/item';
import type { ItemCategoryRecord } from '@/types/item';

/**
 * Groups and sorts items by category or group_name.
 * Items are grouped by the chosen key and sorted within each group by name.
 * @param skipInternalSort - If true, skips sorting items within categories (preserves external sort)
 */
export const groupAndSortItemsByCategory = (
  items: TimelineItem[],
  categories: ItemCategoryRecord[] = [],
  skipInternalSort: boolean = false,
  groupBy: 'category' | 'group' = 'category'
): TimelineItem[] => {
  if (!items.length) return items;

  // Group items by the chosen key
  const groupedItems = items.reduce((groups, item) => {
    const key = groupBy === 'group'
      ? ((item as any).group_name || 'Chưa phân nhóm')
      : (item.category || 'Uncategorized');
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {} as Record<string, TimelineItem[]>);

  // Sort items within each group by sort_order, fallback to name
  // Skip internal sort if items are already sorted externally (e.g., by date)
  if (!skipInternalSort) {
    Object.keys(groupedItems).forEach(key => {
      groupedItems[key].sort((a, b) => {
        // Use sort_order if both items have it
        if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
          return a.sortOrder - b.sortOrder;
        }
        // Fallback to alphabetical sorting if sort_order is missing
        return a.name.localeCompare(b.name);
      });
    });
  }

  // Sort category groups by sort_order from database, fallback to alphabetical
  const categoryMap = categories.reduce((map, cat) => {
    map[cat.name] = cat.sort_order;
    return map;
  }, {} as Record<string, number>);

  const sortedKeys = Object.keys(groupedItems).sort((a, b) => {
    if (groupBy === 'group') {
      if (a === 'Chưa phân nhóm') return 1;
      if (b === 'Chưa phân nhóm') return -1;
      return a.localeCompare(b, 'vi');
    }
    const orderA = categoryMap[a] ?? 999;
    const orderB = categoryMap[b] ?? 999;
    if (orderA !== orderB) return orderA - orderB;
    return a.localeCompare(b);
  });

  // Flatten back to array with groups maintained
  const result: TimelineItem[] = [];
  sortedKeys.forEach(key => {
    result.push(...groupedItems[key]);
  });

  return result;
};